import { 
    Flex, 
    Box, 
    Heading, 
    Text, 
    Icon,
    VStack,
    HStack,
    Container,
    Wrap,
    WrapItem
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { FiZap, FiShield, FiTrendingUp } from 'react-icons/fi';
import SignInForm from '../../components/forms/auth/SignInForm';

const MotionBox = motion(Box);
const MotionFlex = motion(Flex);

export default function SignIn() {
    const features = [
        { icon: FiZap, title: '使いやすい', description: '直感的な画面' },
        { icon: FiShield, title: '通知管理', description: '重要な通知を見逃さない' },
        { icon: FiTrendingUp, title: '便利', description: 'スキルを管理しやすい' },
    ];

    return (
        <Flex minH="100vh" bg="#0a0e27">
            {/* Left Side - Dynamic Brand Section */}
            <Box
                flex={{ base: '0', lg: '1' }}
                display={{ base: 'none', lg: 'block' }}
                position="relative"
                overflow="hidden"
            >
                {/* Animated Gradient Background */}
                <Box
                    position="absolute"
                    top="-50%"
                    left="-50%"
                    right="-50%"
                    bottom="-50%"
                    bgGradient="conic-gradient(from 0deg at 50% 50%, #ff6b6b 0deg, #ee5a6f 72deg, #c44569 144deg, #6c5ce7 216deg, #a29bfe 288deg, #ff6b6b 360deg)"
                    animation="rotate 20s linear infinite"
                    opacity="0.15"
                    filter="blur(60px)"
                    sx={{
                        '@keyframes rotate': {
                            '0%': { transform: 'rotate(0deg)' },
                            '100%': { transform: 'rotate(360deg)' }
                        }
                    }}
                />

                {/* Grid Pattern */}
                <Box
                    position="absolute"
                    inset="0"
                    bgImage="linear-gradient(rgba(255,255,255,0.02) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.02) 1px, transparent 1px)"
                    bgSize="50px 50px"
                    opacity="0.4"
                />

                <Container maxW="container.md" position="relative" zIndex={1} h="full">
                    <Flex 
                        direction="column"
                        justify="center" 
                        h="100vh"
                        px={12}
                    >
                        {/* Animated Logo */}
                        <MotionBox
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8 }}
                            mb={12}
                        >
                            <HStack spacing={4} mb={4}>
                                <Box
                                    position="relative"
                                    w="70px"
                                    h="70px"
                                >
                                    <Box
                                        position="absolute"
                                        inset="0"
                                        bgGradient="linear(135deg, #667eea 0%, #764ba2 100%)"
                                        borderRadius="20px"
                                        transform="rotate(0deg)"
                                        transition="all 0.3s"
                                        _groupHover={{ transform: 'rotate(180deg)' }}
                                    />
                                    <Flex
                                        position="relative"
                                        w="full"
                                        h="full"
                                        align="center"
                                        justify="center"
                                    >
                                        <Text 
                                            fontSize="3xl" 
                                            fontWeight="black"
                                            color="white"
                                        >
                                            ⚡
                                        </Text>
                                    </Flex>
                                </Box>
                                <Box>
                                    <Text 
                                        fontSize="3xl" 
                                        fontWeight="900"
                                        color="white"
                                        letterSpacing="tight"
                                        lineHeight="1"
                                    >
                                        SUN SKILL SHEET
                                    </Text>
                                    <Text 
                                        fontSize="xs" 
                                        color="whiteAlpha.700"
                                        fontWeight="700"
                                        letterSpacing="widest"
                                        textTransform="uppercase"
                                        mt={1}
                                    >
                                        Powered by 経営企画本部
                                    </Text>
                                </Box>
                            </HStack>
                        </MotionBox>

                        {/* Dynamic Headline */}
                        <MotionBox
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.2, duration: 0.8 }}
                            mb={8}
                        >
                            <Heading 
                                fontSize={{ base: '4xl', xl: '6xl' }}
                                fontWeight="900"
                                lineHeight="1.1"
                                mb={6}
                            >
                                <Text
                                    as="span"
                                    display="block"
                                    bgGradient="linear(to-r, #667eea, #764ba2, #f093fb)"
                                    bgClip="text"
                                    mb={2}
                                >
                                    スキルシートを作成
                                </Text>
                                <Text
                                    as="span"
                                    color="white"
                                >
                                    しましょう！
                                </Text>
                            </Heading>
                            <Text 
                                fontSize="lg" 
                                color="whiteAlpha.800"
                                fontWeight="500"
                                lineHeight="1.8"
                                maxW="520px"
                            >
                                簡単に、迅速に、そして効果的に私たちのプラットフォームであなたのスキルを最大限に引き出しましょう。
                            </Text>
                        </MotionBox>

                        {/* Feature Cards */}
                        <Wrap spacing={4} mb={12}>
                            {features.map((feature, index) => (
                                <WrapItem key={index}>
                                    <MotionBox
                                        initial={{ opacity: 0, scale: 0.8 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        transition={{ delay: 0.4 + index * 0.1, duration: 0.5 }}
                                        whileHover={{ scale: 1.05, y: -4 }}
                                    >
                                        <Box
                                            bg="whiteAlpha.100"
                                            backdropFilter="blur(20px)"
                                            borderRadius="2xl"
                                            p={5}
                                            border="1px solid"
                                            borderColor="whiteAlpha.200"
                                            w="180px"
                                            transition="all 0.3s"
                                            _hover={{
                                                bg: 'whiteAlpha.150',
                                                borderColor: 'whiteAlpha.300',
                                                boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)'
                                            }}
                                        >
                                            <Box
                                                bg="whiteAlpha.200"
                                                w="fit-content"
                                                p={2}
                                                borderRadius="lg"
                                                mb={3}
                                            >
                                                <Icon 
                                                    as={feature.icon} 
                                                    w={5} 
                                                    h={5} 
                                                    color="white"
                                                />
                                            </Box>
                                            <Text 
                                                color="white" 
                                                fontSize="md"
                                                fontWeight="800"
                                                mb={1}
                                            >
                                                {feature.title}
                                            </Text>
                                            <Text 
                                                color="whiteAlpha.700" 
                                                fontSize="xs"
                                                fontWeight="600"
                                            >
                                                {feature.description}
                                            </Text>
                                        </Box>
                                    </MotionBox>
                                </WrapItem>
                            ))}
                        </Wrap>


                        {/* Floating Orbs */}
                        <Box 
                            position="absolute" 
                            top="20%" 
                            left="10%" 
                            w="200px" 
                            h="200px" 
                            borderRadius="full" 
                            bgGradient="radial-gradient(circle, rgba(102, 126, 234, 0.3), transparent)" 
                            filter="blur(60px)" 
                            animation="float 8s ease-in-out infinite" 
                            sx={{ 
                                '@keyframes float': { 
                                    '0%, 100%': { transform: 'translateY(0)' }, 
                                    '50%': { transform: 'translateY(-20px)' } 
                                } 
                            }} 
                        />
                        <Box 
                            position="absolute" 
                            bottom="30%" 
                            right="15%" 
                            w="150px" 
                            h="150px" 
                            borderRadius="full" 
                            bgGradient="radial-gradient(circle, rgba(240, 147, 251, 0.3), transparent)" 
                            filter="blur(60px)" 
                            animation="float 6s ease-in-out infinite" 
                            sx={{ 
                                '@keyframes float': { 
                                    '0%, 100%': { transform: 'translateY(0)' }, 
                                    '50%': { transform: 'translateY(20px)' } 
                                } 
                            }} 
                        />
                    </Flex>
                </Container>
            </Box>

            {/* Right Side - Modern Form */}
            <MotionFlex
                flex={{ base: '1', lg: '0 0 600px' }}
                align="center"
                justify="center"
                bg="white"
                position="relative"
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
            >
                {/* Subtle Grain Texture */}
                <Box
                    position="absolute"
                    inset="0"
                    opacity="0.03"
                    //bgImage="url('data:image/svg+xml,%3Csvg viewBox=\"0 0 200 200\" xmlns=\"http://www.w3.org/2000/svg\"%3E%3Cfilter id=\"noiseFilter\"%3E%3CfeTurbulence type=\"fractalNoise\" baseFrequency=\"0.9\" numOctaves=\"3\" stitchTiles=\"stitch\"/%3E%3C/filter%3E%3Crect width=\"100%25\" height=\"100%25\" filter=\"url(%23noiseFilter)\"/%3E%3C/svg%3E')"
                    pointerEvents="none"
                />

                <Box
                    w="full"
                    maxW="480px"
                    px={12}
                    py={16}
                >
                    {/* Mobile Logo */}
                    <Box 
                        display={{ base: 'flex', lg: 'none' }}
                        mb={10}
                        justifyContent="center"
                    >
                        <HStack spacing={3}>
                            <Box
                                w="50px"
                                h="50px"
                                bgGradient="linear(135deg, #667eea, #764ba2)"
                                borderRadius="16px"
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                boxShadow="0 4px 20px rgba(102, 126, 234, 0.3)"
                            >
                                <Text fontSize="2xl" fontWeight="black" color="white">
                                    ⚡
                                </Text>
                            </Box>
                            <Text 
                                fontSize="2xl" 
                                fontWeight="900"
                                bgGradient="linear(to-r, #667eea, #764ba2)"
                                bgClip="text"
                            >
                                SUN SKILL SHEET
                            </Text>
                        </HStack>
                    </Box>

                    {/* Form Header */}
                    <VStack spacing={4} align="start" mb={10}>
                        <Box>
                            <Text 
                                fontSize="xs"
                                fontWeight="800"
                                color="purple.500"
                                letterSpacing="widest"
                                textTransform="uppercase"
                                mb={3}
                            >
                                Welcome
                            </Text>
                            <Heading 
                                fontSize="4xl"
                                fontWeight="900"
                                color="gray.900"
                                letterSpacing="tight"
                                lineHeight="1.2"
                            >
                                サインインして
                                <Text as="span" display="block" bgGradient="linear(to-r, purple.600, pink.500)" bgClip="text">
                                    始めましょう
                                </Text>
                            </Heading>
                        </Box>
                        <Text 
                            color="gray.600" 
                            fontSize="md"
                            fontWeight="500"
                        >
                            アカウント情報を入力してください
                        </Text>
                    </VStack>

                    {/* Form */}
                    <SignInForm />

                    {/* Trust Badge */}
                    <HStack 
                        mt={10} 
                        justify="center" 
                        spacing={4}
                        pt={8}
                        borderTop="1px solid"
                        borderColor="gray.100"
                    >
                        <Icon as={FiShield} color="green.500" w={4} h={4} />
                        <Text fontSize="xs" color="gray.500" fontWeight="600">
                            POWERED BY 経営企画本部
                        </Text>
                    </HStack>
                </Box>
            </MotionFlex>
        </Flex>
    );
}